'use client';

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ProductCard from '@/components/ui/ProductCard';
import PageHeader from '@/components/ui/PageHeader';
import { Product } from '@/types';
import JsonLd from '@/components/ui/JsonLd';
import { generateBreadcrumbSchema } from '@/lib/schema';
import { fetchProducts } from '@/lib/api';
import { ListFilter, Search, X } from 'lucide-react';
import SearchModal from '@/components/ui/SearchModal';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { SITE_URL } from '@/lib/site';

// Sıralama seçenekleri
const sortOptions = [
  { value: 'default', label: 'Önerilen Sıralama' },
  { value: 'newest', label: 'En Yeniler' },
  { value: 'name_asc', label: 'İsme Göre' },
];

type AllProductsFiltersFieldsProps = {
  draftSearch: string;
  setDraftSearch: (q: string) => void;
  onApplySearch: () => void;
  sortOption: string;
  setSortOption: (v: string) => void;
  onReset: () => void;
  onOpenRichSearch: () => void;
};

function AllProductsFiltersFields({
  draftSearch,
  setDraftSearch,
  onApplySearch,
  sortOption,
  setSortOption,
  onReset,
  onOpenRichSearch,
}: AllProductsFiltersFieldsProps) {
  return (
    <>
      <div className="mb-8">
        <label htmlFor="all-products-search" className="block mb-2 text-sm font-medium text-gray-700">
          Ürün Ara
        </label>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onApplySearch();
          }}
          className="space-y-3"
        >
          <div className="relative">
            <input
              type="search"
              id="all-products-search"
              autoComplete="off"
              enterKeyHint="search"
              placeholder="Ürün adı yazın..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 min-h-[44px]"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
            />
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              aria-hidden
            />
          </div>
          <button
            type="submit"
            className="w-full py-2.5 px-4 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Listede Ara
          </button>
        </form>
        <button
          type="button"
          onClick={onOpenRichSearch}
          className="mt-3 w-full text-sm text-red-700 hover:text-red-800 underline-offset-2 hover:underline text-center py-1"
        >
          Önizlemeli ara ve kategoriler
        </button>
      </div>

      <div className="mb-8">
        <h3 className="font-medium text-gray-900 mb-4">Sıralama</h3>
        <div className="space-y-3">
          {sortOptions.map((option) => (
            <label key={option.value} className="flex items-center cursor-pointer group min-h-[44px]">
              <input
                type="radio"
                name="allProductsSort"
                checked={sortOption === option.value}
                onChange={() => setSortOption(option.value)}
                className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500 shrink-0"
              />
              <span className="ml-3 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="w-full py-2.5 px-4 text-sm font-medium text-red-950 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-all duration-200 flex items-center justify-center gap-2 min-h-[44px]"
      >
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Filtreleri Sıfırla
      </button>
    </>
  );
}

export type ProductsPageClientProps = {
  initialProducts: Product[];
  initialTotalPages: number;
  initialTotalProducts: number;
};

export default function ProductsPageClient({
  initialProducts,
  initialTotalPages,
  initialTotalProducts,
}: ProductsPageClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const urlSearchQuery = searchParams.get('search') || '';

  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortOption, setSortOption] = useState('default');
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalProducts, setTotalProducts] = useState(initialTotalProducts);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftSearch, setDraftSearch] = useState(urlSearchQuery);
  const [isMounted, setIsMounted] = useState(false);

  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  useEffect(() => {
    setDraftSearch(urlSearchQuery);
  }, [urlSearchQuery]);

  const pushWithSearch = useCallback(
    (q: string) => {
      const p = new URLSearchParams(searchParams.toString());
      if (q.trim()) p.set('search', q.trim());
      else p.delete('search');
      const s = p.toString();
      router.push(s ? `${pathname}?${s}` : pathname);
    },
    [pathname, router, searchParams]
  );

  const applySearchFromDrawer = useCallback(() => {
    pushWithSearch(draftSearch);
    closeFilters();
  }, [draftSearch, pushWithSearch, closeFilters]);

  const resetFilters = useCallback(() => {
    setSortOption('default');
    setPage(1);
    router.push(pathname);
    closeFilters();
  }, [pathname, router, closeFilters]);

  useEffect(() => {
    setPage(1);
  }, [urlSearchQuery, sortOption]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Ürünleri yükle
  useEffect(() => {
    if (!isMounted) return;

    // Eğer ilk yüklemedeki varsayılan durum ise API'ye tekrar gitme
    if (urlSearchQuery === '' && sortOption === 'default' && page === 1) {
      setProducts(initialProducts);
      setTotalPages(initialTotalPages);
      setTotalProducts(initialTotalProducts);
      setLoading(false);
      return;
    }

    const fetchProductsData = async () => {
      setLoading(true);
      try {
        const result = await fetchProducts(
          page,
          70,
          undefined, // kategori yok
          undefined, // öne çıkan
          urlSearchQuery, // arama
          sortOption // sıralama
        );

        if (!result.success) {
          throw new Error('Ürünler yüklenirken bir hata oluştu');
        }

        setProducts(result.data.products);
        setTotalPages(result.data.pagination.totalPages || 1);
        setTotalProducts(result.data.pagination.total || 0);
      } catch (err) {
        console.error('Ürün verisi çekme hatası:', err);
        setError('Ürünler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchProductsData();
  }, [urlSearchQuery, sortOption, page, isMounted, initialProducts, initialTotalPages, initialTotalProducts]);

  useEffect(() => {
    if (!isMounted) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page, isMounted]);

  useEffect(() => {
    if (!filtersOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFilters();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [filtersOpen, closeFilters]);

  const openRichSearch = useCallback(() => {
    closeFilters();
    setSearchModalOpen(true);
  }, [closeFilters]);

  // Breadcrumb öğeleri
  const breadcrumbItems = [
    { name: 'Ana Sayfa', url: SITE_URL },
    { name: 'Ürünler', url: `${SITE_URL}/urunler` },
  ];
  
  // Arama başlığını görüntüle
  const searchTitle = urlSearchQuery 
    ? `"${urlSearchQuery}" için Arama Sonuçları` 
    : 'Tüm Ürünler';

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* JSON-LD Şeması */}
      <JsonLd schema={generateBreadcrumbSchema(breadcrumbItems)} />
      
      <div className="container mx-auto px-4">
        <PageHeader 
          title="Ürün Kataloğumuz" 
          subtitle="Berber ve kuaför salonları için profesyonel mobilya çözümleri"
          theme="dark"
          size="medium"
          breadcrumbs={[
            { label: 'Ürünler', url: '/urunler' }
          ]}
        />

        {/* Ürün Listesi */}
        <div className="py-12">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 min-w-0">
              {searchTitle}
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({totalProducts} ürün)
              </span>
            </h2>
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 shadow-sm hover:border-red-300 hover:text-red-600 transition-colors"
            >
              <ListFilter className="h-4 w-4 shrink-0" aria-hidden />
              Filtreler
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
                {products.map((product: Product) => (
                  <ProductCard 
                    key={product.id}
                    id={product.id}
                    name={product.name}
                    image={product.images?.[0]}
                    category={product.category}
                    discountPercentage={product.discountPercentage}
                    slug={product.slug}
                    description={product.description}
                    features={product.features}
                    images={product.images}
                    price={product.price}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex justify-center items-center mt-10 space-x-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  <span className="mx-2 text-gray-700">
                    Sayfa {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <svg 
                className="w-16 h-16 text-gray-300 mb-4"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-medium text-gray-700 mb-2">Hiç ürün bulunamadı</h2>
              <p className="text-gray-500 max-w-md">
                {urlSearchQuery 
                  ? `"${urlSearchQuery}" araması için ürün bulunamadı. Lütfen farklı bir arama terimi deneyin.`
                  : 'Henüz ürün bulunmuyor.'}
              </p>
              {urlSearchQuery && (
                <Link
                  href="/urunler"
                  className="mt-6 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Tüm Ürünleri Göster
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {filtersOpen &&
        createPortal(
          <div className="fixed inset-0 z-[160] isolate">
            <div
              role="presentation"
              className="absolute inset-0 bg-black/50 touch-manipulation"
              onClick={closeFilters}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="all-products-filter-drawer-title"
              className="absolute top-0 right-0 bottom-0 z-[170] flex w-[min(24rem,calc(100vw-2.5rem))] max-w-full flex-col bg-white shadow-2xl [-webkit-overflow-scrolling:touch]"
            >
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 pb-4 shrink-0 pt-[max(1rem,env(safe-area-inset-top))]">
                <h2 id="all-products-filter-drawer-title" className="text-lg font-semibold text-gray-900">
                  Filtreler
                </h2>
                <button
                  type="button"
                  onClick={closeFilters}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 touch-manipulation"
                  aria-label="Filtreleri kapat"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-y-contain p-6 pb-4">
                <AllProductsFiltersFields
                  draftSearch={draftSearch}
                  setDraftSearch={setDraftSearch}
                  onApplySearch={applySearchFromDrawer}
                  sortOption={sortOption}
                  setSortOption={setSortOption}
                  onReset={resetFilters}
                  onOpenRichSearch={openRichSearch}
                />
              </div>
              <div className="shrink-0 border-t border-gray-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden">
                <button
                  type="button"
                  onClick={closeFilters}
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 shadow-sm touch-manipulation"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Arama Modalı */}
      {isSearchModalOpen && (
        <SearchModal onClose={() => setSearchModalOpen(false)} />
      )}
    </div>
  );
}
